<template>
  <form @submit.prevent="save" novalidate autocomplete="off">
    <div class="modal-card" style="width:1100px; max-width:100%">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ isNew ? 'Add Q&A' : 'Edit Q&A' }}</p>
        <b-tag v-if="!isNew" rounded :type="form.status === 'live' ? 'is-success' : 'is-warning'">
          {{ form.status === 'live' ? 'Live' : 'Draft' }}
        </b-tag>
      </header>
      <section class="modal-card-body">
        <!-- Loading -->
        <div v-if="loading" class="py-xl">
          <loading message="Loading entry..." />
        </div>
        <div v-else class="columns">
          <!-- Form -->
          <div class="column is-7">
            <b-message v-if="nonFieldErrors.length" type="is-danger" size="is-small" class="mb-md">
              <p v-for="(message, index) in nonFieldErrors" :key="index">{{ message }}</p>
            </b-message>
            <!-- Title -->
            <b-field label="Question" :type="fieldType('title')" :message="fieldMessage('title')">
              <b-input v-model="form.title" placeholder="e.g. How do I add products?" maxlength="256" @input="syncSlug" />
            </b-field>
            <!-- Slug -->
            <b-field label="Slug" :type="fieldType('slug')" :message="fieldMessage('slug') || 'Stable identifier used by the assistant. Generated from the question; fixed once created.'">
              <b-input v-model="form.slug" maxlength="64" :disabled="!isNew" @input="slugTouched = true" />
            </b-field>
            <div class="columns">
              <!-- Group -->
              <div class="column">
                <b-field label="Group" :type="fieldType('group_id')" :message="fieldMessage('group_id') || 'The topic pill this question is listed under.'">
                  <b-select v-model="form.group_id" expanded placeholder="Select a group">
                    <option v-for="group in groups" :key="group.id" :value="group.id">{{ group.name }}</option>
                  </b-select>
                </b-field>
              </div>
              <!-- Categories -->
              <div class="column">
                <b-field label="Categories" :type="fieldType('category_ids')" :message="fieldMessage('category_ids') || 'Pages where this question appears in the menu. Search finds it everywhere.'">
                  <div class="pt-xs">
                    <b-checkbox
                      v-for="category in categories"
                      :key="category.id"
                      v-model="form.category_ids"
                      :native-value="category.id"
                      class="mr-md mb-xs">
                      {{ category.label }}
                    </b-checkbox>
                  </div>
                </b-field>
              </div>
            </div>
            <!-- Keywords -->
            <b-field label="Keywords" :type="fieldType('keywords')" :message="fieldMessage('keywords') || 'Alternate phrasings that should match this question in search. Press Enter after each.'">
              <b-taginput
                v-model="form.keywords"
                ellipsis
                icon="label"
                maxlength="64"
                placeholder="Add a keyword"
                aria-close-label="Remove keyword"
                :before-adding="validateKeyword" />
            </b-field>
            <!-- Related Entries -->
            <b-field label="Related Questions" :type="fieldType('related_entry_ids')" :message="fieldMessage('related_entry_ids') || 'Suggested as follow-up chips after this answer.'">
              <b-taginput
                v-model="form.related"
                :data="relatedOptions"
                autocomplete
                open-on-focus
                field="title"
                icon="link-variant"
                placeholder="Search for a question..."
                :allow-new="false"
                :loading="relatedLoading"
                @typing="searchRelated">
                <!-- Custom tags: question titles are long, so let them wrap
                     instead of Bulma's default single-line (nowrap) tag. -->
                <template #selected="{ tags }">
                  <b-tag
                    v-for="(tag, index) in tags"
                    :key="tag.id"
                    closable
                    aria-close-label="Remove related question"
                    style="height:auto; white-space:normal; max-width:100%; text-align:left"
                    @close="removeRelated(index)">
                    {{ tag.title }}
                  </b-tag>
                </template>
                <template v-slot="props">
                  <span>{{ props.option.title }}</span>
                  <span v-if="props.option.group" class="has-text-grey is-size-7"> &middot; {{ props.option.group.name }}</span>
                  <span v-if="props.option.status === 'draft'" class="has-text-warning is-size-7"> &middot; draft</span>
                </template>
                <template #empty>
                  {{ relatedLoading ? 'Searching...' : 'No matching questions.' }}
                </template>
              </b-taginput>
            </b-field>
            <!-- Answer -->
            <b-field label="Answer" :type="fieldType('answer_html')" :message="fieldMessage('answer_html')">
              <AnswerEditor v-model="form.answer_html" />
            </b-field>
            <!-- Status -->
            <div class="has-background-white-ter rounded-lg pa-md">
              <b-switch v-model="isLive" type="is-success">
                <span class="has-text-weight-bold">{{ isLive ? 'Live' : 'Draft' }}</span>
              </b-switch>
              <p class="is-size-7 has-text-grey mt-xs">
                <span v-if="isLive">Visible to suppliers in the assistant as soon as you save.</span>
                <span v-else>Hidden from suppliers. Switch to Live when the answer is ready.</span>
              </p>
            </div>
          </div>
          <!-- Preview -->
          <div class="column is-5">
            <ChatbotPreview :entry="previewEntry" :status="form.status" :assistant="assistant" />
          </div>
        </div>
      </section>
      <footer class="modal-card-foot" style="justify-content:space-between">
        <div>
          <ConfirmButton
            v-if="!isNew && !loading"
            initialLabel="Delete"
            type="is-danger"
            :loading="deleting"
            :disabled="saving"
            @action:confirmed="remove" />
        </div>
        <div class="buttons is-marginless is-vcentered">
          <span v-if="!loading && !isValid" class="is-size-7 has-text-grey mr-md">
            Add {{ missingFields.join(', ') }} to save.
          </span>
          <button class="button" type="button" @click="$parent.close()">Close</button>
          <b-button
            native-type="submit"
            type="is-primary"
            :loading="saving"
            :disabled="!isValid || saving || loading || deleting">
            {{ isNew ? 'Add Q&A' : 'Save Changes' }}
          </b-button>
        </div>
      </footer>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import AnswerEditor from './AnswerEditor'
import ChatbotPreview from './ChatbotPreview'
import ConfirmButton from '@/components/ConfirmButton'

const SLUG_MAX_LENGTH = 64

// Matches the seeded slugs (e.g. 'faq-how-do-i-add-products').
const slugify = (value) => {
  const body = String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (!body) return ''
  return ('faq-' + body).slice(0, SLUG_MAX_LENGTH).replace(/-+$/g, '')
}

const stripTags = (html) => String(html || '').replace(/<[^>]*>/g, '').trim()

const emptyForm = () => ({
  slug: '',
  title: '',
  answer_html: '',
  group_id: null,
  status: 'draft',
  keywords: [],
  category_ids: [],
  related: []
})

export default {
  components: {
    AnswerEditor,
    ChatbotPreview,
    ConfirmButton
  },
  computed: {
    ...mapGetters(['api', 'assistantConfig']),
    // Header copy for the preview, from the same config the chatbot renders.
    assistant () {
      return (this.assistantConfig && this.assistantConfig.assistant) || {}
    },
    isNew () {
      return this.entry === null
    },
    endpoint () {
      let route = this.api.baseUrl + '/v1/assistant/entries/'
      if (!this.isNew) {
        route += this.entry.id + '/'
      }
      return route
    },
    method () {
      return this.isNew ? 'post' : 'patch'
    },
    json () {
      return {
        slug: this.form.slug.trim(),
        title: this.form.title.trim(),
        answer_html: this.form.answer_html,
        group_id: this.form.group_id,
        status: this.form.status,
        keywords: this.form.keywords,
        category_ids: this.form.category_ids,
        related_entry_ids: this.form.related.map(related => related.id)
      }
    },
    isLive: {
      get () {
        return this.form.status === 'live'
      },
      set (value) {
        this.form.status = value ? 'live' : 'draft'
      }
    },
    // Fields still needed before the entry can be saved; empty = valid.
    missingFields () {
      const missing = []
      if (this.form.title.trim() === '') missing.push('a question')
      if (this.form.slug.trim() === '') missing.push('a slug')
      if (this.form.group_id === null) missing.push('a group')
      if (stripTags(this.form.answer_html) === '') missing.push('an answer')
      return missing
    },
    isValid () {
      return this.missingFields.length === 0
    },
    groupName () {
      const group = (this.groups || []).find(group => group.id === this.form.group_id)
      return group ? group.name : ''
    },
    // The entry in the shape the chatbot renders (see build_config in the API).
    previewEntry () {
      return {
        title: this.form.title,
        group: this.groupName,
        answer: this.form.answer_html,
        related: this.form.related.map(related => related.title)
      }
    },
    nonFieldErrors () {
      const messages = []
      const collect = (value) => {
        if (Array.isArray(value)) {
          value.forEach(collect)
        } else if (typeof value === 'string') {
          messages.push(value)
        }
      }
      collect(this.errors.non_field_errors)
      collect(this.errors.detail)
      return messages
    }
  },
  data () {
    return {
      // Editing re-fetches the entry, so start in the loading state instead of
      // rendering the form and then tearing it down (which destroys the editor
      // before it has been handed to EditorContent).
      loading: this.entry !== null,
      saving: false,
      deleting: false,
      errors: {},
      form: emptyForm(),
      // Once the slug has been hand-edited it stops tracking the title.
      slugTouched: false,
      relatedOptions: [],
      relatedLoading: false,
      relatedTimer: null
    }
  },
  methods: {
    fieldType (field) {
      return (field in this.errors) ? 'is-danger' : ''
    },
    fieldMessage (field) {
      if (!(field in this.errors)) return ''
      const value = this.errors[field]
      return Array.isArray(value) ? value.join(' ') : String(value)
    },
    syncSlug () {
      if (this.isNew && !this.slugTouched) {
        this.form.slug = slugify(this.form.title)
      }
    },
    removeRelated (index) {
      this.form.related.splice(index, 1)
    },
    validateKeyword (keyword) {
      const value = String(keyword).trim()
      if (!value) return false
      return !this.form.keywords.some(existing => existing.toLowerCase() === value.toLowerCase())
    },
    searchRelated (text) {
      clearTimeout(this.relatedTimer)
      const query = String(text || '').trim()
      if (query.length < 2) {
        this.relatedOptions = []
        return
      }
      this.relatedLoading = true
      this.relatedTimer = setTimeout(() => {
        this.$http.get(this.api.baseUrl + '/v1/assistant/entries/', { params: { search: query, limit: 10, order_by: 'title' } }).then(response => {
          const excluded = this.form.related.map(related => related.id)
          if (!this.isNew) excluded.push(this.entry.id)
          this.relatedOptions = response.data.results
            .filter(result => !excluded.includes(result.id))
            .map(result => ({ id: result.id, title: result.title, status: result.status, group: result.group }))
        }).finally(() => {
          this.relatedLoading = false
        })
      }, 250)
    },
    load () {
      this.loading = true
      this.$http.get(this.endpoint).then(response => {
        this.populate(response.data)
      }).finally(() => {
        this.loading = false
      })
    },
    populate (data) {
      this.form = {
        slug: data.slug || '',
        title: data.title || '',
        answer_html: data.answer_html || '',
        group_id: data.group ? data.group.id : null,
        status: data.status || 'draft',
        keywords: Array.isArray(data.keywords) ? data.keywords.slice() : [],
        category_ids: (data.categories || []).map(category => category.id),
        related: (data.related_entries || []).map(related => ({ id: related.id, title: related.title, status: related.status }))
      }
      this.slugTouched = true
    },
    save () {
      if (!this.isValid) return
      this.saving = true
      this.errors = {}
      this.$http[this.method](this.endpoint, this.json).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: this.isNew ? 'Q&A added!' : 'Q&A saved!' })
        this.$emit('updated', response.data)
        this.$parent.close()
      }).catch(error => {
        if (error.response && error.response.status === 400) {
          this.errors = error.response.data
        }
        this.$buefy.toast.open({ type: 'is-danger', message: 'Please review your input and try again.' })
      }).finally(() => {
        this.saving = false
      })
    },
    remove () {
      this.deleting = true
      this.$http.delete(this.endpoint).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Q&A deleted.' })
        this.$emit('deleted', this.entry)
        this.$parent.close()
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'The Q&A could not be deleted.' })
      }).finally(() => {
        this.deleting = false
      })
    }
  },
  mounted () {
    this.$store.dispatch('loadAssistantConfig')
    if (!this.isNew) {
      this.load()
    } else if (this.prefill !== null) {
      this.form.title = this.prefill.title || ''
      this.syncSlug()
    }
  },
  beforeDestroy () {
    clearTimeout(this.relatedTimer)
  },
  props: {
    // null to create; an entry row (from the list) to edit.
    entry: {
      type: Object,
      default: null
    },
    // Optional initial values when creating (e.g. from a missed question).
    prefill: {
      type: Object,
      default: null
    },
    groups: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    }
  }
}
</script>
